import {
  AREA_TYPES,
  AREA_TYPE_COLORS,
  AREA_TYPE_LABELS,
  DEFAULT_PRODUCTION_RATES,
  LABOR_CATEGORIES,
  MODIFIER_MULTIPLIERS,
  resolveAreaType,
} from '../../SiteTakeoff/siteTakeoffConstants';

export const FREQUENCY_PRESETS = {
  weekly: { label: 'Weekly', visitsPerYear: 52, family: 'weekly' },
  biweekly: { label: 'Bi-weekly', visitsPerYear: 26, family: 'weekly' },
  monthly: { label: 'Monthly', visitsPerYear: 12, family: 'monthly' },
  bimonthly: { label: 'Bimonthly', visitsPerYear: 6, family: 'bimonthly' },
  quarterly: { label: 'Quarterly', visitsPerYear: 4, family: 'quarterly' },
  '3x-yr': { label: '3x / yr', visitsPerYear: 3, family: 'yr' },
  '2x-yr': { label: '2x / yr', visitsPerYear: 2, family: 'yr' },
  annual: { label: 'Annual', visitsPerYear: 1, family: 'yr' },
};

export const DEFAULT_ESTIMATE_SETTINGS = {
  minMarginPct: 45,
  laborRates: {
    Maint: 38.5,
    Arborist: 70,
    Herbicide: 75.5,
    Irrigation: 95.5,
  },
  frequencies: FREQUENCY_PRESETS,
  modifierTable: {
    obstructed: MODIFIER_MULTIPLIERS.access_obstructed,
    sloped: MODIFIER_MULTIPLIERS.slope_sloped,
    'high-litter': MODIFIER_MULTIPLIERS.litterExposure_high,
    'med-weed': MODIFIER_MULTIPLIERS.weedPressure_med,
    'high-weed': MODIFIER_MULTIPLIERS.weedPressure_high,
    'limited-access': MODIFIER_MULTIPLIERS.limitedAccess,
    'blow-restrictions': MODIFIER_MULTIPLIERS.blowRestrictions,
    'hand-tools': 1.5,
  },
  irrigationZoneInference: {
    treesPerDripZone: 30,
    bedSFPerDripZone: 4000,
    flowerSFPerDripZone: 2500,
    turfSFPerSprayZone: 3500,
    backflowZonesPerSite: 1,
    minZonesPerSite: 3,
    minutesPerZone: 12,
  },
  subcontractors: {
    preEmergent: {
      vendorId: 'desert-spray',
      vendorName: 'Desert Spray Co.',
      unitRate: 400,
      unit: 'acre',
      minPerVisit: 500,
      frequency: '3x-yr',
    },
  },
};

const ROLE_BY_CATEGORY = {
  mtc: 'Maint',
  arborist: 'Arborist',
  herbicide: 'Herbicide',
  irrigation: 'Irrigation',
};

const CATEGORY_BY_ROLE = Object.entries(ROLE_BY_CATEGORY)
  .reduce((acc, [category, role]) => ({ ...acc, [role]: category }), {});

const PRE_EMERGENT_TYPES = new Set([
  AREA_TYPES.GR_CLEAN,
  AREA_TYPES.GR_DESERT_LOW,
  AREA_TYPES.GR_DESERT_MED,
  AREA_TYPES.GR_DESERT_HIGH,
  AREA_TYPES.BD_SHRUB_LOW,
  AREA_TYPES.BD_SHRUB_MED,
  AREA_TYPES.BD_SHRUB_HIGH,
]);

const TURF_TYPES = new Set([
  AREA_TYPES.NT_TURF_OPEN,
  AREA_TYPES.NT_TURF_OBSTRUCTED,
  AREA_TYPES.ST_SYNTHETIC,
]);

const FLOWER_TYPES = new Set([AREA_TYPES.BD_FLOWER_COLOR]);

const BED_TYPES = new Set([
  AREA_TYPES.GR_DESERT_LOW,
  AREA_TYPES.GR_DESERT_MED,
  AREA_TYPES.GR_DESERT_HIGH,
  AREA_TYPES.BD_SHRUB_LOW,
  AREA_TYPES.BD_SHRUB_MED,
  AREA_TYPES.BD_SHRUB_HIGH,
  AREA_TYPES.GC_GROUNDCOVER,
]);

const round = (value, places = 2) => {
  const factor = 10 ** places;
  return Math.round((Number(value) || 0) * factor) / factor;
};

const safeNumber = (value, fallback = 0) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
};

export const getFrequency = (frequency, settings = DEFAULT_ESTIMATE_SETTINGS) => {
  if (frequency && typeof frequency === 'object') return frequency;
  return settings.frequencies?.[frequency] || FREQUENCY_PRESETS[frequency] || FREQUENCY_PRESETS.weekly;
};

export const getVisitsPerYear = (frequency, settings = DEFAULT_ESTIMATE_SETTINGS) =>
  safeNumber(getFrequency(frequency, settings).visitsPerYear, 0);

export const roleForCategory = (category) => ROLE_BY_CATEGORY[category] || category || 'Maint';

export const categoryForRole = (role) => CATEGORY_BY_ROLE[role] || role;

export const formatServiceLabel = (serviceCode) =>
  DEFAULT_PRODUCTION_RATES[serviceCode]?.label ||
  String(serviceCode || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

export const modifierKeysFromArea = (area = {}) => {
  const modifiers = area.modifiers || {};
  const keys = [];
  if (modifiers.access === 'obstructed') keys.push('obstructed');
  if (modifiers.slope === 'sloped') keys.push('sloped');
  if (modifiers.litterExposure === 'high') keys.push('high-litter');
  if (modifiers.weedPressure === 'med') keys.push('med-weed');
  if (modifiers.weedPressure === 'high') keys.push('high-weed');
  if (modifiers.limitedAccess) keys.push('limited-access');
  if (modifiers.blowRestrictions) keys.push('blow-restrictions');
  return keys;
};

export function computeTask(task, modifierTable = DEFAULT_ESTIMATE_SETTINGS.modifierTable, settings = DEFAULT_ESTIMATE_SETTINGS) {
  const modifierMultiplier = (task.modifiers || [])
    .reduce((product, key) => product * safeNumber(modifierTable[key], 1), 1);
  const hoursPerVisit = task.ratePerHour > 0
    ? (safeNumber(task.quantity) / safeNumber(task.ratePerHour)) * safeNumber(task.coverage, 1) * modifierMultiplier
    : 0;
  const visitsPerYear = getVisitsPerYear(task.frequency, settings);
  return {
    hoursPerVisit,
    annualHours: hoursPerVisit * visitsPerYear,
  };
}

export function computeService(service, settings = DEFAULT_ESTIMATE_SETTINGS) {
  if (!service?.enabled) {
    return { role: service?.role, hours: 0, cost: 0, perVisitCost: 0, visitsPerYear: 0 };
  }

  if (service.delivery === 'subcontracted') {
    const visitsPerYear = getVisitsPerYear(service.sub?.frequency, settings);
    const perVisitCost = Math.max(
      safeNumber(service.sub?.quantity) * safeNumber(service.sub?.unitRate),
      safeNumber(service.sub?.minPerVisit)
    );
    return {
      role: service.role,
      hours: 0,
      cost: perVisitCost * visitsPerYear,
      perVisitCost,
      visitsPerYear,
    };
  }

  const hours = (service.tasks || []).reduce((sum, task) => {
    const computed = task.hoursPerVisit != null && task.annualHours != null
      ? task
      : computeTask(task, settings.modifierTable, settings);
    return sum + safeNumber(computed.annualHours);
  }, 0);

  return { role: service.role, hours, cost: 0, perVisitCost: 0 };
}

export function dominantVisitsPerYear(estimate, tier, settings = DEFAULT_ESTIMATE_SETTINGS) {
  const services = (estimate.services || [])
    .filter(service => service.enabled && tier.includedServices.includes(service.role));
  const visits = services.flatMap(service => {
    if (service.delivery === 'subcontracted') return [getVisitsPerYear(service.sub?.frequency, settings)];
    return (service.tasks || []).map(task => getVisitsPerYear(task.frequency, settings));
  }).filter(Boolean);
  return visits.length ? Math.max(...visits) : 12;
}

export function effectiveRate(role, estimate, tier, settings = DEFAULT_ESTIMATE_SETTINGS) {
  return safeNumber(
    tier.rateOverrides?.[role] ??
    estimate.rateOverrides?.[role] ??
    settings.laborRates?.[role],
    0
  );
}

export function computeTier(estimate, tier, settings = DEFAULT_ESTIMATE_SETTINGS) {
  const hoursByRole = {};
  let laborCost = 0;
  let subCost = 0;

  (estimate.services || []).forEach((service) => {
    if (!service.enabled || !tier.includedServices.includes(service.role)) return;
    const serviceResult = computeService(service, settings);
    if (service.delivery === 'subcontracted') {
      subCost += serviceResult.cost;
      return;
    }
    hoursByRole[service.role] = (hoursByRole[service.role] || 0) + serviceResult.hours;
  });

  Object.entries(hoursByRole).forEach(([role, hours]) => {
    laborCost += hours * effectiveRate(role, estimate, tier, settings);
  });

  const totalCost = laborCost + subCost;
  const marginPct = Math.min(95, Math.max(0, safeNumber(tier.marginPct)));
  const annualPrice = marginPct < 100 ? totalCost / (1 - marginPct / 100) : 0;
  const visitsPerYear = dominantVisitsPerYear(estimate, tier, settings);

  return {
    hoursByRole,
    laborCost,
    subCost,
    totalCost,
    annualPrice,
    monthlyPrice: annualPrice / 12,
    perVisitPrice: visitsPerYear > 0 ? annualPrice / visitsPerYear : 0,
    effectiveMargin: marginPct,
  };
}

export function solveMarginForTarget(estimate, tier, settings = DEFAULT_ESTIMATE_SETTINGS, target = tier.targetPrice) {
  if (!target || safeNumber(target.amount) <= 0) return null;
  const tierCost = computeTier(estimate, { ...tier, targetPrice: undefined }, settings).totalCost;
  const annualTarget = target.basis === 'per-visit'
    ? safeNumber(target.amount) * dominantVisitsPerYear(estimate, tier, settings)
    : safeNumber(target.amount) * 12;
  if (annualTarget <= 0) return null;
  return round((1 - tierCost / annualTarget) * 100, 1);
}

export function runSanityChecks(estimate, settings = DEFAULT_ESTIMATE_SETTINGS, portfolioStats = {}) {
  const laborDriftOk = Object.entries(estimate.rateOverrides || {}).every(([role, rate]) => {
    const base = safeNumber(settings.laborRates?.[role]);
    if (!base) return true;
    return Math.abs(safeNumber(rate) - base) / base <= 0.05;
  });

  const marginsOk = (estimate.tiers || []).every(tier => safeNumber(tier.marginPct) >= safeNumber(settings.minMarginPct, 45));
  const mappedOk = !safeNumber(estimate.unmappedAreaCount, 0);
  const lowCoverageTasks = (estimate.services || []).flatMap(service => service.tasks || [])
    .filter(task => safeNumber(task.coverage, 1) < 0.2 && !task.coverageConfirmed);
  const highFrequencyTasks = (estimate.services || []).flatMap(service => service.tasks || [])
    .filter((task) => {
      const median = safeNumber(portfolioStats?.serviceMedianAnnualHours?.[task.serviceCode]);
      if (!median) return false;
      const annual = safeNumber(task.annualHours ?? computeTask(task, settings.modifierTable, settings).annualHours);
      return annual > median * 3;
    });

  return [
    {
      key: 'rates',
      status: laborDriftOk ? 'pass' : 'warn',
      label: 'Hourly rates within +/-5% of company defaults',
    },
    {
      key: 'margin',
      status: marginsOk ? 'pass' : 'warn',
      label: `Margin meets ${settings.minMarginPct || 45}% minimum threshold`,
    },
    {
      key: 'mapping',
      status: mappedOk ? 'pass' : 'warn',
      label: 'All scope areas mapped and assigned a service',
    },
    {
      key: 'frequency',
      status: lowCoverageTasks.length === 0 && highFrequencyTasks.length === 0 ? 'pass' : 'warn',
      label: lowCoverageTasks.length
        ? 'Coverage below 20% needs confirmation before sending'
        : 'Low Pruning frequency is unusually high - confirm before sending',
    },
  ];
}

const getAreas = (takeoffOrAreas) => {
  if (Array.isArray(takeoffOrAreas)) return takeoffOrAreas;
  return takeoffOrAreas?.areas || [];
};

const areaSqft = (area) => Math.max(0, safeNumber(area.sqft ?? area.area));

const areaTreeCount = (area) =>
  safeNumber(area.treeCount ?? area.plantDensity?.treeCount) +
  safeNumber(area.palmCount ?? area.plantDensity?.palmCount);

const densityFactor = (density) => ({
  none: 0.05,
  low: 0.2,
  med: 0.45,
  high: 0.75,
}[density] ?? 0.2);

export function inferIrrigationZones(takeoff, settings = DEFAULT_ESTIMATE_SETTINGS) {
  const areas = getAreas(takeoff).filter(area => !area.parentAreaId);
  const cfg = settings.irrigationZoneInference || DEFAULT_ESTIMATE_SETTINGS.irrigationZoneInference;

  let trees = 0;
  let bedSF = 0;
  let flowerSF = 0;
  let turfSF = 0;

  areas.forEach((area) => {
    const type = resolveAreaType(area.areaType);
    const sqft = areaSqft(area);
    trees += areaTreeCount(area);
    if (FLOWER_TYPES.has(type)) flowerSF += safeNumber(area.flowerSF, sqft);
    if (TURF_TYPES.has(type)) turfSF += safeNumber(area.turfSF, sqft);
    if (BED_TYPES.has(type)) {
      const explicit = safeNumber(area.shrubSF) + safeNumber(area.groundcoverSF);
      bedSF += explicit || sqft * densityFactor(area.plantDensity?.shrubDensity || area.plantDensity?.groundcoverDensity);
    }
  });

  const treeZones = Math.ceil(trees / safeNumber(cfg.treesPerDripZone, 30));
  const bedZones = Math.ceil(bedSF / safeNumber(cfg.bedSFPerDripZone, 4000));
  const flowerZones = Math.ceil(flowerSF / safeNumber(cfg.flowerSFPerDripZone, 2500));
  const turfZones = Math.ceil(turfSF / safeNumber(cfg.turfSFPerSprayZone, 3500));
  const backflowZones = safeNumber(cfg.backflowZonesPerSite, 1);
  const rawZones = treeZones + bedZones + flowerZones + turfZones + backflowZones;
  const totalZones = Math.max(rawZones, safeNumber(cfg.minZonesPerSite, 3));

  return {
    breakdown: [
      { label: `${round(trees, 0)} trees / palms`, color: '#7a3a2e', quantity: treeZones, math: `/${cfg.treesPerDripZone} per zone` },
      { label: `${round(bedSF, 0).toLocaleString()} SF shrubs / groundcover`, color: '#d18227', quantity: bedZones, math: `/${cfg.bedSFPerDripZone} SF per zone` },
      { label: `${round(flowerSF, 0).toLocaleString()} SF flowers`, color: '#ec407a', quantity: flowerZones, math: `/${cfg.flowerSFPerDripZone} SF per zone` },
      { label: `${round(turfSF, 0).toLocaleString()} SF turf`, color: '#6f9f4e', quantity: turfZones, math: `/${cfg.turfSFPerSprayZone} SF per zone` },
      { label: 'Backflow + master valve check', color: '#cec3a5', quantity: backflowZones, math: 'site allowance' },
    ],
    totalZones,
    rawZones,
    minutesPerZone: safeNumber(cfg.minutesPerZone, 12),
  };
}

export function inferPreEmergentAcreage(takeoff, settings = DEFAULT_ESTIMATE_SETTINGS) {
  const areas = getAreas(takeoff).filter(area => !area.parentAreaId);
  const buckets = {};
  let totalSF = 0;

  areas.forEach((area) => {
    const type = resolveAreaType(area.areaType);
    const hasTag = Array.isArray(area.tags) && area.tags.includes('pre-emergent');
    const hasService = Array.isArray(area.servicesRequired) && area.servicesRequired.includes('pre_emergent');
    if (!PRE_EMERGENT_TYPES.has(type) && !hasTag && !hasService) return;
    const sqft = areaSqft(area);
    totalSF += sqft;
    if (!buckets[type]) {
      buckets[type] = {
        label: AREA_TYPE_LABELS[type] || type || 'Mapped area',
        color: AREA_TYPE_COLORS[type] || '#cec3a5',
        areas: 0,
        sqft: 0,
      };
    }
    buckets[type].areas += 1;
    buckets[type].sqft += sqft;
  });

  const totalAcres = totalSF / 43560;
  const sub = settings.subcontractors?.preEmergent || DEFAULT_ESTIMATE_SETTINGS.subcontractors.preEmergent;
  const perVisitCost = Math.max(totalAcres * safeNumber(sub.unitRate), safeNumber(sub.minPerVisit));
  const minApplied = perVisitCost === safeNumber(sub.minPerVisit) && totalAcres * safeNumber(sub.unitRate) < safeNumber(sub.minPerVisit);

  return {
    breakdown: Object.values(buckets),
    totalSF,
    totalAcres,
    perVisitCost,
    annualCost: perVisitCost * getVisitsPerYear(sub.frequency, settings),
    minApplied,
  };
}

export function buildServiceLinesFromTakeoff(areas, hoursResult, settings = DEFAULT_ESTIMATE_SETTINGS) {
  const preEmergent = inferPreEmergentAcreage(areas, settings);
  const irrigation = inferIrrigationZones(areas, settings);

  const serviceLines = Object.entries(LABOR_CATEGORIES).map(([category, meta]) => {
    const role = roleForCategory(category);
    const categoryHours = hoursResult?.byCategory?.[category] || {};
    const routineTasks = Object.entries(hoursResult?.routineByService || {})
      .filter(([, task]) => task.category === category)
      .map(([serviceCode, task]) => ({
        serviceCode,
        label: formatServiceLabel(serviceCode),
        areaCount: task.areaCount,
        ratePerHour: DEFAULT_PRODUCTION_RATES[serviceCode]?.rate || 1,
        quantity: 0,
        coverage: task.coveragePerVisit,
        frequency: 'weekly',
        hoursPerVisit: task.totalHoursPerApplication,
        annualHours: task.totalHoursPerApplication * 52,
      }));
    const periodicTasks = (categoryHours.periodicServices || []).map((task) => ({
      serviceCode: task.service,
      label: formatServiceLabel(task.service),
      areaCount: task.areaCount,
      ratePerHour: DEFAULT_PRODUCTION_RATES[task.service]?.rate || 1,
      quantity: 0,
      coverage: task.coveragePerVisit,
      frequency: task.visitsPerYear === 12 ? 'monthly' : task.visitsPerYear === 6 ? 'bimonthly' : task.visitsPerYear === 3 ? '3x-yr' : task.visitsPerYear === 2 ? '2x-yr' : { label: `${task.visitsPerYear}x / yr`, visitsPerYear: task.visitsPerYear },
      hoursPerVisit: task.hoursPerApplication,
      annualHours: task.annualHours,
    }));
    return {
      role,
      name: meta.label,
      enabled: (categoryHours.annualTotal || 0) > 0,
      delivery: 'labor',
      routineTasks,
      periodicTasks,
      tasks: [...routineTasks, ...periodicTasks],
    };
  });

  const herbicideSub = settings.subcontractors?.preEmergent || DEFAULT_ESTIMATE_SETTINGS.subcontractors.preEmergent;
  if (preEmergent.totalAcres > 0) {
    const herbIndex = serviceLines.findIndex(line => line.role === 'Herbicide');
    serviceLines[herbIndex] = {
      role: 'Herbicide',
      name: 'Herbicide',
      enabled: true,
      delivery: 'subcontracted',
      tasks: [],
      routineTasks: [],
      periodicTasks: [],
      inference: preEmergent,
      sub: {
        ...herbicideSub,
        quantity: preEmergent.totalAcres,
      },
    };
  }

  const irrigationIndex = serviceLines.findIndex(line => line.role === 'Irrigation');
  if (irrigationIndex >= 0 && irrigation.totalZones > 0 && serviceLines[irrigationIndex].tasks.length === 0) {
    const cfg = settings.irrigationZoneInference || DEFAULT_ESTIMATE_SETTINGS.irrigationZoneInference;
    const hoursPerVisit = irrigation.totalZones * safeNumber(cfg.minutesPerZone, 12) / 60 * 0.5;
    serviceLines[irrigationIndex] = {
      ...serviceLines[irrigationIndex],
      enabled: true,
      inference: irrigation,
      periodicTasks: [{
        serviceCode: 'irrigation_check',
        label: 'Irrigation Check',
        areaCount: irrigation.totalZones,
        ratePerHour: 60 / safeNumber(cfg.minutesPerZone, 12),
        quantity: irrigation.totalZones,
        coverage: 0.5,
        frequency: 'monthly',
        hoursPerVisit,
        annualHours: hoursPerVisit * 12,
      }],
      tasks: [{
        serviceCode: 'irrigation_check',
        label: 'Irrigation Check',
        areaCount: irrigation.totalZones,
        ratePerHour: 60 / safeNumber(cfg.minutesPerZone, 12),
        quantity: irrigation.totalZones,
        coverage: 0.5,
        frequency: 'monthly',
        hoursPerVisit,
        annualHours: hoursPerVisit * 12,
      }],
    };
  } else if (irrigationIndex >= 0) {
    serviceLines[irrigationIndex] = {
      ...serviceLines[irrigationIndex],
      inference: irrigation,
    };
  }

  return serviceLines;
}
